import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import notesApiService from '../../services/notesApi';
import logger from '../../utils/logging/logger';
import {
  computeWeeklyAssignmentGroups,
  formatDueDate,
  getAssignmentStatus,
  getQuizStatus,
  isOngoingCourseItem,
} from './useDashboard/helpers';
import {
  computeAssignmentSummary,
  computeNotesSummary,
  computeQuizSummary,
  computeRecentAssignments,
  computeRecentNotes,
  computeRecentQuizzes,
} from './useDashboard/summaries';

export function useDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeAssignments: 0,
    completedAssignments: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  // showBackToTop removed - using GlobalScrollToTop from App.js
  const [assignments, setAssignments] = useState([]);
  const [currentWeeklyAssignments, setCurrentWeeklyAssignments] = useState([]);
  const [allActiveAssignments, setAllActiveAssignments] = useState([]); // Store all active assignments for summary calculation
  const [quizzes, setQuizzes] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showPreviousCourses, setShowPreviousCourses] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    // Don't fetch data if user is null
    if (!user) {
      return;
    }

    try {
      // Only make student-specific API calls for students
      const apiCalls = [
        apiService.getDashboardStats(),
        apiService.getRecentActivity(),
        apiService.getCourses(),
        apiService.getAssignments()
      ];

      // Only make student-specific API calls for students
      if (user.role === 'student') {
        apiCalls.push(
          quizApiService.getStudentAssignments(),
          notesApiService.getMyNotes({ pageSize: 10 })
        );
      }

      // CRITICAL FIX: Use Promise.allSettled instead of Promise.all to handle partial failures gracefully
      // This prevents one failed request from causing all responses to be undefined
      const responses = await Promise.allSettled(apiCalls);

      // Extract results, handling both fulfilled and rejected promises
      const statsResponse = responses[0]?.status === 'fulfilled' ? responses[0].value : null;
      const activityResponse = responses[1]?.status === 'fulfilled' ? responses[1].value : null;
      const coursesResponse = responses[2]?.status === 'fulfilled' ? responses[2].value : null;
      const assignmentsResponse = responses[3]?.status === 'fulfilled' ? responses[3].value : null;
      const quizzesResponse = user.role === 'student' && responses[4]?.status === 'fulfilled' ? responses[4].value : null;
      const notesResponse = user.role === 'student' && responses[5]?.status === 'fulfilled' ? responses[5].value : null;

      // CRITICAL FIX: Check if user still exists (might have logged out during fetch)
      if (!user) {
        return; // User logged out, don't process responses
      }

      if (statsResponse?.success && statsResponse.stats) {
        setStats(statsResponse.stats);
      }

      if (activityResponse?.success && activityResponse.activities) {
        setRecentActivities(activityResponse.activities);
      }

      if (coursesResponse?.success && coursesResponse.courses) {
        // Filter only enrolled courses
        const enrolled = coursesResponse.courses.filter(course => course.is_enrolled);
        setEnrolledCourses(enrolled);
      }

      if (assignmentsResponse?.success && assignmentsResponse.assignments) {
        // Filter to only show assignments from currently ongoing courses.
        const activeAssignments = assignmentsResponse.assignments.filter(isOngoingCourseItem);
        // Store all active assignments for summary calculation
        setAllActiveAssignments(activeAssignments);

        // Separate weekly assignments that are due this week
        const { weeklyDueThisWeek, otherAssignments } = computeWeeklyAssignmentGroups(activeAssignments);
        setCurrentWeeklyAssignments(weeklyDueThisWeek);
        setAssignments(otherAssignments);
      }

      // Only process student-specific responses for students
      if (user.role === 'student') {
        if (quizzesResponse?.success && quizzesResponse.data) {
          setQuizzes(quizzesResponse.data);
        }

        if (notesResponse?.success && notesResponse.items) {
          setNotes(notesResponse.items);
        }
      }
    } catch (error) {
      // CRITICAL FIX: Don't log errors if user has logged out (401 is expected)
      // Check if error is due to logout/authentication
      const isAuthError = error?.status === 401 ||
                         error?.message?.includes('Token revoked') ||
                         error?.message?.includes('session has expired') ||
                         error?.message?.includes('logged out');

      // Only log non-auth errors (auth errors during logout are expected)
      if (!isAuthError) {
        logger.error('Error fetching dashboard data', {
          error,
          errorMessage: error?.message,
          errorStack: error?.stack,
          errorName: error?.name
        });
        console.error('Dashboard error details:', error);
      }

      // Only update state if user still exists (don't update if they logged out)
      if (user) {
        setStats({
          totalCourses: 0,
          activeAssignments: 0,
          completedAssignments: 0
        });
        setRecentActivities([]);
        setEnrolledCourses([]);
        setAssignments([]);
        setQuizzes([]);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    } else {
      // Clear all state when user is null
      setStats(null);
      setRecentActivities([]);
      setEnrolledCourses([]);
      setAssignments([]);
      setQuizzes([]);
    }
  }, [user, fetchDashboardData]);

  // Ensure page starts at top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Scroll handlers removed - using GlobalScrollToTop from App.js

  // Memoized computed values for performance (must be called before any early returns)
  const assignmentSummary = useMemo(
    () => computeAssignmentSummary(assignments, currentWeeklyAssignments, allActiveAssignments, getAssignmentStatus),
    [assignments, currentWeeklyAssignments, allActiveAssignments]
  );

  const recentAssignments = useMemo(
    () => computeRecentAssignments(assignments, currentWeeklyAssignments, getAssignmentStatus),
    [assignments, currentWeeklyAssignments]
  );

  const quizSummary = useMemo(
    () => computeQuizSummary(quizzes, getQuizStatus),
    [quizzes]
  );

  const recentQuizzes = useMemo(
    () => computeRecentQuizzes(quizzes),
    [quizzes]
  );

  const notesSummary = useMemo(
    () => computeNotesSummary(notes),
    [notes]
  );

  const recentNotes = useMemo(
    () => computeRecentNotes(notes),
    [notes]
  );

  // Render instructor dashboard if user is an instructor

  return {
    allActiveAssignments,
    assignmentSummary,
    enrolledCourses,
    formatDueDate,
    getAssignmentStatus,
    getQuizStatus,
    navigate,
    notesSummary,
    quizSummary,
    quizzes,
    recentActivities,
    recentAssignments,
    recentNotes,
    recentQuizzes,
    setShowPreviousCourses,
    showPreviousCourses,
    stats,
    user,
  };
}
